import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset as offsetMw,
  shift,
  size,
  useDismiss,
  useFloating,
  useInteractions,
  type Placement,
} from '@floating-ui/react';
import { useEffect, useLayoutEffect, useState, type CSSProperties, type ReactNode } from 'react';

export type Anchor = HTMLElement | DOMRect | { getBoundingClientRect(): DOMRect } | null;

export interface PopoverProps {
  open: boolean;
  onClose: () => void;
  anchor: Anchor;
  placement?: Placement;
  offset?: number;
  width?: number | string;
  maxHeight?: number;
  className?: string;
  style?: CSSProperties;
  /** Close when clicking outside (default true). */
  dismissOnOutside?: boolean;
  /** Close on Escape (default true). */
  dismissOnEscape?: boolean;
  testId?: string;
  children: ReactNode;
  initialFocus?: boolean;
}

/**
 * An element anchor that keeps its last place once it hides (a sidebar row's ••• shows only while the row is
 * hovered): the popover then stays put instead of jumping to the window's corner, where a hidden element is.
 */
function steadyReference(el: HTMLElement) {
  let last: DOMRect | null = null;
  return {
    contextElement: el,
    getBoundingClientRect() {
      const r = el.getBoundingClientRect();
      if (el.isConnected && (r.width > 0 || r.height > 0)) last = r;
      return last ?? r;
    },
  };
}

function toReference(anchor: Anchor) {
  if (!anchor) return null;
  if (anchor instanceof HTMLElement) return anchor;
  if (typeof DOMRect !== 'undefined' && anchor instanceof DOMRect) return { getBoundingClientRect: () => anchor };
  return anchor as { getBoundingClientRect(): DOMRect };
}

export function Popover({
  open,
  onClose,
  anchor,
  placement = 'bottom-start',
  offset = 4,
  width,
  maxHeight,
  className,
  style,
  dismissOnOutside = true,
  dismissOnEscape = true,
  testId,
  children,
}: PopoverProps) {
  const [state, setState] = useState<'opening' | 'open'>('opening');
  const { refs, floatingStyles, context, isPositioned } = useFloating({
    open,
    onOpenChange: (o) => {
      if (!o) onClose();
    },
    placement,
    // Follow the anchor on scroll and window resize, but not the popover's own size changes: a menu
    // placed above its anchor would otherwise shift under the pointer when its content grows.
    whileElementsMounted: (reference, floating, update) => autoUpdate(reference, floating, update, { elementResize: false }),
    middleware: [
      offsetMw(offset),
      flip({ padding: 8 }),
      shift({ padding: 8 }),
      size({
        padding: 8,
        apply({ availableHeight, elements }) {
          elements.floating.style.maxHeight = `${Math.min(maxHeight ?? 10_000, Math.max(120, availableHeight))}px`;
        },
      }),
    ],
  });
  const dismiss = useDismiss(context, { outsidePress: dismissOnOutside, escapeKey: dismissOnEscape, bubbles: { escapeKey: false } });
  const { getFloatingProps } = useInteractions([dismiss]);

  useLayoutEffect(() => {
    const ref = toReference(anchor);
    if (ref instanceof HTMLElement) {
      refs.setReference(ref);
      refs.setPositionReference(steadyReference(ref));
    } else refs.setPositionReference(ref);
  }, [anchor, refs]);

  // The fade/scale-in starts once the popover has its final position, and it takes no clicks before
  // that, so it never appears or reacts at a provisional spot. (It stays focusable for autofocus.)
  useEffect(() => {
    if (!open) {
      setState('opening');
      return;
    }
    if (!isPositioned) return;
    const raf = requestAnimationFrame(() => setState('open'));
    return () => cancelAnimationFrame(raf);
  }, [open, isPositioned]);

  if (!open || !anchor) return null;
  return (
    <FloatingPortal>
      <div
        ref={refs.setFloating}
        className={`lm-popover ${className ?? ''}`}
        data-state={state}
        data-testid={testId}
        style={{ ...floatingStyles, width, display: 'flex', flexDirection: 'column', ...(isPositioned ? {} : { pointerEvents: 'none' }), ...style }}
        {...getFloatingProps()}
      >
        {children}
      </div>
    </FloatingPortal>
  );
}
